import React, { useRef, useState, useEffect } from "react"
import GaugeComponent from "react-gauge-component"

const Gauge = ({
  value = 0,
  name = "",
  min,
  max,
  ranges = [],
  formatValue,
  formatTickLabel,
  newTickLabel = false,
  type,
  titleVisible = true,
}) => {
  const containerRef = useRef(null)
  const [gaugeKey, setGaugeKey] = useState(0)
  const [tickFontSize, setTickFontSize] = useState("11px")
  const [containerWidth, setContainerWidth] = useState(300)
  const prevFontSizeRef = useRef("11px")

  const debounceTimer = useRef(null)

  const computeFontSize = width =>
    width < 180
      ? "0.5rem"
      : width < 240
      ? "0.55rem"
      : width < 300
      ? "0.55rem"
      : width < 380
      ? "0.60rem"
      : "0.65rem"

  const gaugeMarginBottom =
    containerWidth < 200
      ? "10px"
      : containerWidth < 280
      ? "10px"
      : containerWidth < 380
      ? "10px"
      : "10px"

  useEffect(() => {
    const measure = () => {
      const width = containerRef.current?.offsetWidth ?? 300
      setContainerWidth(width)
      const newFontSize = computeFontSize(width)
      if (newFontSize !== prevFontSizeRef.current) {
        prevFontSizeRef.current = newFontSize
        setTickFontSize(newFontSize)
        setGaugeKey(prev => prev + 1)
      }
    }

    const debouncedMeasure = () => {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(measure, 150)
    }

    measure()

    window.addEventListener("resize", debouncedMeasure)

    const pageContent = document.querySelector(".page-content")
    if (pageContent) pageContent.addEventListener("transitionend", debouncedMeasure)

    return () => {
      clearTimeout(debounceTimer.current)
      window.removeEventListener("resize", debouncedMeasure)
      if (pageContent) pageContent.removeEventListener("transitionend", debouncedMeasure)
    }
  }, [])

  const typeConfig = {
    average: {
      label: "Avg",
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
      icon: "⌀",
    },
    sum: {
      label: "Sum",
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#15803d",
      icon: "Σ",
    },
  }
  const typeStyle = typeConfig[type?.toLowerCase()] || null

  const ticksFromRanges = [
    {
      value: min,
      label: newTickLabel && formatTickLabel ? formatTickLabel(min) : `${min}`,
    },
    ...ranges.map(r => ({
      value: r.limit,
      label:
        newTickLabel && formatTickLabel
          ? formatTickLabel(r.limit)
          : `${r.limit}`,
    })),
  ]

  const getStatusColor = () => {
    if (!ranges.length) return "#94a3b8"
    if (value <= ranges[0]?.limit) return "#ef4444"
    if (value <= ranges[1]?.limit) return "#f59e0b"
    return "#10b981"
  }

  const statusColor = getStatusColor()
  const professionalRanges = ranges.map((r, i) => ({
    ...r,
    color: i === 0 ? "#ef4444" : i === 1 ? "#f59e0b" : "#10b981",
  }))

  const isReady =
    min !== undefined && max !== undefined && max > min && ranges.length > 0

  if (!isReady) return <div className="gauge-loading">Loading...</div>

  return (
    <div className="gauge-container" ref={containerRef}>
      {titleVisible && <h6 className="gauge-title">{name}</h6>}

      <div className="gauge-chart">
        <GaugeComponent
          key={gaugeKey}
          value={value}
          type="semicircle"
          minValue={min}
          maxValue={max}
          style={{
            width: "100%",
            height: "100%",
            marginBottom: gaugeMarginBottom,
          }}
          arc={{
            width: 0.15,
            padding: 0.02,
            cornerRadius: 2,
            subArcs: professionalRanges,
            emptyColor: "#e6e6e6",
          }}
          pointer={{ color: "#555", length: 0.75, width: 10 }}
          labels={{
            valueLabel: { hide: true },
            tickLabels: {
              hideMinMax: true,
              ticks: ticksFromRanges,
              defaultTickValueConfig: {
                style: {
                  fontSize: tickFontSize,
                  fill: "#000",
                  fontWeight: "bold",
                },
                formatTextValue:
                  newTickLabel && formatTickLabel ? formatTickLabel : undefined,
              },
              defaultTickLineConfig: { length: 4, color: "#475569" },
            },
          }}
        />
      </div>

      <div className="gauge-footer">
        <h4 className="value-text" style={{ color: statusColor }}>
          {formatValue ? formatValue(value) : `${value}`}
        </h4>
        {typeStyle && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "3px 10px",
              borderRadius: "20px",
              background: typeStyle.bg,
              border: `1px solid ${typeStyle.border}`,
            }}
          >
            <span style={{ fontSize: "11px", color: typeStyle.color }}>
              {typeStyle.icon}
            </span>
            <span
              style={{
                fontSize: "10px",
                fontWeight: "700",
                color: typeStyle.color,
                letterSpacing: "1px",
              }}
            >
              {typeStyle.label}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default Gauge
